/**
 * Shared CONTACTS icon registry + URI builder — used by BOTH the private Canvas
 * editor and the public Grid publication layer so a Territory's contact block
 * renders and links identically in both surfaces.
 *
 * Kind tokens mirror the backend `CONTACT_KINDS` allow-list. The final href is
 * DERIVED here from (kind, value) so the persisted datum stays clean and the
 * link behaviour is centralized (mailto: / tel: / wa.me / https / maps search).
 */
import type { IconType } from "react-icons";
import { FaEnvelope, FaPhone, FaGlobe, FaLocationDot } from "react-icons/fa6";
import { SiWhatsapp } from "react-icons/si";

import type { ContactKind } from "../domain/canvas-model";

export const CONTACT_ICONS: Record<ContactKind, IconType> = {
  EMAIL: FaEnvelope,
  PHONE: FaPhone,
  WHATSAPP: SiWhatsapp,
  WEBSITE: FaGlobe,
  MAP: FaLocationDot,
};

export const CONTACT_BRAND_COLORS: Record<ContactKind, string> = {
  EMAIL: "#EA4335",
  PHONE: "#34A853",
  WHATSAPP: "#25D366",
  WEBSITE: "#22d3ee",
  MAP: "#4285F4",
};

export const CONTACT_KIND_LABELS: Record<ContactKind, string> = {
  EMAIL: "Email", PHONE: "Phone", WHATSAPP: "WhatsApp", WEBSITE: "Website", MAP: "Address",
};

export const CONTACT_KIND_OPTIONS: ContactKind[] = ["EMAIL", "PHONE", "WHATSAPP", "WEBSITE", "MAP"];

/** Build the safe href for a typed contact datum. */
export function contactHref(kind: ContactKind, value: string): string | null {
  const v = (value ?? "").trim();
  if (!v) return null;
  switch (kind) {
    case "EMAIL":
      return `mailto:${v}`;
    case "PHONE":
      return `tel:${v.replace(/[^\d+]/g, "")}`;
    case "WHATSAPP":
      return `https://wa.me/${v.replace(/[^\d]/g, "")}`;
    case "WEBSITE":
      return v.startsWith("https://") ? v : `https://${v.replace(/^https?:\/\//, "")}`;
    case "MAP":
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(v)}`;
    default:
      return null;
  }
}
